import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ShoppingBag, MapPin } from 'lucide-react';
const NAMES = ['María José', 'Valentina R.', 'Camila G.', 'Sofía M.', 'Isabella V.', 'Luciana M.', 'Daniela F.', 'Fernanda L.', 'Andrea C.', 'Gabriela M.', 'Carolina P.', 'Alejandra G.', 'Paola J.', 'Natalia R.', 'Mariana T.', 'Jimena S.', 'Ana Paula V.', 'Karla D.', 'Diana R.', 'Mónica H.', 'Laura S.', 'Patricia O.', 'Claudia N.', 'Verónica C.', 'Adriana P.', 'Lorena A.', 'Catalina M.', 'Renata D.', 'Ximena F.', 'Paulina R.', 'Estefanía L.', 'Marisol C.', 'Rosa M.', 'Carmen L.', 'Elena G.', 'Lucía T.', 'Ana María B.', 'Silvia R.', 'Gloria P.', 'Teresa V.'];
const CITIES = ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Bogotá', 'Medellín', 'Lima', 'Buenos Aires', 'Santiago', 'Quito', 'San José', 'Puebla', 'Cancún', 'Cartagena', 'Barranquilla', 'Cali', 'Arequipa', 'Córdoba', 'Rosario', 'Valparaíso', 'Guayaquil', 'Mérida', 'León', 'Querétaro', 'Tijuana', 'Panamá', 'Santo Domingo', 'Tegucigalpa', 'San Salvador', 'Managua', 'La Paz'];
interface Product {
  name: string;
  price: string;
  image: string;
}
const PRODUCTS: Product[] = [{
  name: 'Mamá Digital 2.0',
  price: '$6.39',
  image: 'https://cdn.magicpatterns.com/uploads/oPjbSiyoEGgPdKx1TT2bff/bono_1.png'
}, {
  name: 'Paquete Completo',
  price: '$12.39',
  image: 'https://cdn.magicpatterns.com/uploads/1idxmYwwtZbiXSe3Yuj4dx/bono_2.png'
}, {
  name: 'Paquete Premium',
  price: '$18.39',
  image: 'https://cdn.magicpatterns.com/uploads/1SJ87rmaR1AxATNiHAPpKu/bono_3.png'
}];
function pickWeightedProduct(): Product {
  const rand = Math.random();
  if (rand < 0.3) return PRODUCTS[0]; // 30% → $6.39
  if (rand < 0.8) return PRODUCTS[1]; // 50% → $12.39
  return PRODUCTS[2]; // 20% → $18.39
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function timeAgo(): string {
  const mins = Math.floor(Math.random() * 12) + 1;
  return `hace ${mins} min`;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: Product;
  time: string;
}
export function SalesPop() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const recentNames = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getUniqueName = useCallback((): string => {
    let name: string;
    let attempts = 0;
    do {
      name = pickRandom(NAMES);
      attempts++;
    } while (recentNames.current.includes(name) && attempts < 20);
    recentNames.current.push(name);
    if (recentNames.current.length > 10) {
      recentNames.current.shift();
    }
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const name = getUniqueName();
    const city = pickRandom(CITIES);
    const product = pickWeightedProduct();
    const time = timeAgo();
    idCounter.current++;
    setNotification({
      id: idCounter.current,
      name,
      city,
      product,
      time
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    // Schedule next notification 8-15 seconds after this one appears
    const nextDelay = (Math.random() * 7 + 8) * 1000;
    timeoutRef.current = setTimeout(showNotification, nextDelay);
  }, [getUniqueName]);
  useEffect(() => {
    // First notification after 3 seconds
    timeoutRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showNotification]);
  if (!notification) return null;
  return <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] transition-all duration-500 ease-in-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`} data-id="element-464">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 px-4 py-3 flex items-center gap-3 max-w-sm w-max" data-id="element-465">
        {/* Product Image */}
        <img src={notification.product.image} alt={notification.product.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-466" />

        {/* Info */}
        <div className="flex-1 min-w-0" data-id="element-467">
          <p className="text-sm text-gray-900 leading-tight" data-id="element-468">
            <span className="font-bold" data-id="element-469">{notification.name}</span> compró
          </p>
          <p className="text-sm font-semibold text-brand-pink truncate" data-id="element-470">
            {notification.product.name} — {notification.product.price}
          </p>
          <div className="flex items-center gap-2 mt-0.5" data-id="element-471">
            <span className="flex items-center gap-0.5 text-xs text-gray-400" data-id="element-472">
              <MapPin className="w-3 h-3" data-id="element-473" />
              {notification.city}
            </span>
            <span className="text-xs text-gray-300" data-id="element-474">·</span>
            <span className="text-xs text-gray-400" data-id="element-475">{notification.time}</span>
          </div>
        </div>

        {/* Verified badge */}
        <div className="flex-shrink-0" data-id="element-476">
          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-477">
            <ShoppingBag className="w-3.5 h-3.5 text-emerald-600" data-id="element-478" />
          </div>
        </div>
      </div>
    </div>;
}